import { KPIDirection, KPIUnit } from "../types";

/**
 * How each KPI is presented: the words around the number, and the two facts
 * needed to format it — `unit` and which direction counts as good.
 *
 * This lives here rather than in the API because it is presentation, not data.
 * The engine returns figures and ledger account codes; this decides how a
 * member is told what they mean, and whether a rising line should read as
 * progress. Rewording a definition is a frontend deploy, not an API change.
 *
 * It also gates the page: a KPI the API returns with no entry here is skipped,
 * so shipping a calculation before its card is written never renders a card
 * with blank prose.
 */
export interface KPIConfig {
  name: string;
  source: string;
  unit: KPIUnit;
  direction: KPIDirection;
  definition: string;
  guidance: string;
  /** Reads as the top of the fraction on the card back. */
  numeratorLabel: string;
  denominatorLabel: string;
  multiplier?: string;
  /**
   * True when the numerator is what's left of the denominator after costs
   * come out, as it is for a margin. The worked example then shows the
   * subtraction — "($262K − $155K) ÷ $262K" — instead of a single figure the
   * member would find nowhere in their books.
   */
  numeratorIsRemainder?: boolean;
  note: string;
}

export const KPI_CONFIG: Record<string, KPIConfig> = {
  field_labor_percent: {
    name: "Field labor",
    source: "QuickBooks · P&L",
    unit: "percent",
    direction: "lower_is_better",
    definition:
      "Of every dollar you bring in, this slice pays the techs and salespeople who do the work out in the field.",
    guidance:
      "Rising usually means crews are staffed heavier than the work coming in, or pay is outrunning sales.",
    numeratorLabel: "Gross wages and burden — field techs + salespeople",
    denominatorLabel: "Total revenue",
    multiplier: "× 100",
    note: "Every dollar of pay — base, commission, spiffs, and the payroll burden that follows them — for the people who run calls and sell in the field, divided by all the revenue you collected.",
  },
  office_labor_percent: {
    name: "Office labor",
    source: "QuickBooks · P&L",
    unit: "percent",
    direction: "lower_is_better",
    definition:
      "The share of revenue spent on you and the office team — the people who keep the business running behind the scenes.",
    guidance:
      "If it creeps up over time, office payroll is growing faster than the company is.",
    numeratorLabel: "Gross wages and burden — owner + office team",
    denominatorLabel: "Total revenue",
    multiplier: "× 100",
    note: "Pay for you and the back-office crew — CSRs, dispatch, admin — plus the payroll burden that follows those wages, divided by all the revenue you collected.",
  },
  gross_margin_percent: {
    name: "Gross margin",
    source: "QuickBooks · P&L",
    unit: "percent",
    direction: "higher_is_better",
    definition:
      "What's left from each job after you pay for the labor and materials to do it — before overhead.",
    guidance:
      "This is your engine. A steady or rising line means your pricing and job costs are in good shape.",
    numeratorLabel: "Total revenue − direct costs (labor + materials)",
    denominatorLabel: "Total revenue",
    multiplier: "× 100",
    numeratorIsRemainder: true,
    note: "Start with revenue, then take out the labor and parts it took to do the jobs — what's left is your gross margin. Overhead like office pay, marketing and rent isn't taken out here; that comes off at net profit.",
  },
  net_profit_percent: {
    name: "Net profit",
    source: "QuickBooks · P&L",
    unit: "percent",
    direction: "higher_is_better",
    definition:
      "The money you actually keep after everything is paid — your true bottom line.",
    guidance:
      "A dip here means costs grew faster than sales somewhere above. Trace it back through the other P&L cards.",
    numeratorLabel: "Gross margin − operating expenses ± other",
    denominatorLabel: "Total revenue",
    multiplier: "× 100",
    note: "Take your gross margin and subtract everything else it costs to keep the doors open — rent, trucks, insurance, marketing, office pay. Then anything from outside the day-to-day, like interest or a one-off gain, is added or taken off. What's left is your net income, the same figure your QuickBooks P&L ends on.",
  },
  materials_equipment_percent: {
    name: "Materials & equipment",
    source: "QuickBooks · P&L",
    unit: "percent",
    direction: "lower_is_better",
    definition:
      "The portion of revenue spent on the parts and equipment you install on jobs.",
    guidance:
      "Holding steady is what you want. Spikes can mean rising supplier prices, wasted material, or jobs priced too low for what they cost.",
    numeratorLabel: "Parts and equipment installed on jobs",
    denominatorLabel: "Total revenue",
    multiplier: "× 100",
    note: "What you paid your suppliers for the materials and equipment that went into the work — divided by all the revenue you collected. Labor to install it isn't in here; that's field labor.",
  },
};

export const PNL_SECTION = {
  code: "pnl",
  ordinal: "01",
  title: "Profit & Loss",
  source: "QuickBooks",
  blurb:
    "How your money moves through the business each month — what comes in, what it costs to do the work, and what you keep.",
};
